import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'

// 비로그인 공개 페이지: 설계의뢰 공유 링크 열람
export default function RequestSharePage() {
  const { token } = useParams()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const apiVersion = process.env.REACT_APP_API_VERSION || 'v1'
  const baseUrl = useMemo(() => {
    // 배포는 동일 오리진을 기본 가정(nginx 프록시)
    return `/${apiVersion}`.replace(/\/{2,}/g, '/')
  }, [apiVersion])

  useEffect(() => {
    let ignore = false
    ; (async () => {
      if (!token) return
      setLoading(true)
      setError(null)
      try {
        const res = await axios.get(`${baseUrl}/request-shares/${token}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) setData(payload)
      } catch (e) {
        if (!ignore) setError('링크가 만료되었거나 유효하지 않습니다.')
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => { ignore = true }
  }, [token, baseUrl])

  const attachments = Array.isArray(data?.attachments) ? data.attachments : []
  const targetFile = data?.targetFile || null

  return (
    <section className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">설계의뢰 공유 링크</h1>
        <Link to="/login" className="text-sm text-slate-600 hover:underline">로그인</Link>
      </div>

      {loading && <div className="mt-6 text-sm text-slate-600">불러오는 중...</div>}
      {error && (
        <div className="mt-6 rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <div className="mt-6 space-y-4">
          <div className="rounded border bg-white p-4">
            <div className="text-sm text-slate-600">원수사</div>
            <div className="font-semibold">{data.insurerOrgName || data.insurerOrgId || '-'}</div>
            <div className="mt-3 text-sm text-slate-600">제목</div>
            <div className="text-slate-900">{data.requestTitle || '-'}</div>
            {data.commonNote && (
              <>
                <div className="mt-3 text-sm text-slate-600">공통 메모</div>
                <div className="whitespace-pre-wrap text-slate-900">{data.commonNote}</div>
              </>
            )}
            {data.targetNote && (
              <>
                <div className="mt-3 text-sm text-slate-600">원수사 개별 메모</div>
                <div className="whitespace-pre-wrap text-slate-900">{data.targetNote}</div>
              </>
            )}
          </div>

          {targetFile && targetFile.downloadUrl && (
            <div className="rounded border bg-white p-4">
              <div className="font-semibold">설계 결과 파일</div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <div className="text-sm text-slate-700">
                  {targetFile.filename || targetFile.fileId}
                </div>
                <a
                  className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                  href={targetFile.downloadUrl}
                >
                  다운로드
                </a>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                링크는 만료/회수될 수 있으며, 외부 공유를 금지합니다.
              </div>
            </div>
          )}

          <div className="rounded border bg-white p-4">
            <div className="font-semibold">첨부파일</div>
            {attachments.length === 0 ? (
              <div className="mt-2 text-sm text-slate-500">첨부파일이 없습니다.</div>
            ) : (
              <div className="mt-2 space-y-2">
                {attachments.map(a => (
                  <div key={a.id} className="flex items-center justify-between gap-3 border rounded px-3 py-2">
                    <div className="text-sm text-slate-700">
                      {a.filename || `첨부 #${a.id}`}
                      {a.expired && (
                        <span className="ml-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5">
                          다운로드 기간만료
                        </span>
                      )}
                    </div>
                    <a
                      className={`rounded border px-3 py-1 text-sm ${a.expired ? 'opacity-50 pointer-events-none' : 'hover:bg-slate-50'}`}
                      href={a.downloadUrl}
                    >
                      다운로드
                    </a>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 text-xs text-slate-500">
              본 링크는 특정 설계의뢰/보험사에 한정된 열람 권한입니다. 외부 공유를 금지합니다.
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

